import axios from "axios";

// This is a frontend-only build. If REACT_APP_BACKEND_URL is set (see
// frontend/.env.example), form submissions are posted to that backend as
// normal. If it is NOT set, submitForm() below falls back to a local
// (in-browser) submission path so every form still opens, validates, and
// "submits" successfully with no network errors — nothing is silently
// broken just because no backend is connected.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API_BASE = `${BACKEND_URL}/api`;
const HAS_BACKEND = Boolean(process.env.REACT_APP_BACKEND_URL);

export const api = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
  timeout: 10000,
});

const LOCAL_STORAGE_PREFIX = "iiutcstudy_submissions:";

function readLocal(endpoint) {
  try {
    return JSON.parse(localStorage.getItem(LOCAL_STORAGE_PREFIX + endpoint) || "[]");
  } catch {
    return [];
  }
}

function writeLocal(endpoint, entries) {
  try {
    localStorage.setItem(LOCAL_STORAGE_PREFIX + endpoint, JSON.stringify(entries));
  } catch {
    // localStorage unavailable (private browsing, quota, etc.) — ignore,
    // the submission still "succeeds" for the purposes of the UI.
  }
}

/**
 * Submits a form payload to `endpoint` (e.g. "/contact", "/newsletter").
 *
 * - If REACT_APP_BACKEND_URL is configured, this posts to the real backend
 *   via `api` and returns/throws exactly like axios would.
 * - If no backend is configured, it simulates a short network round-trip
 *   and stores the submission in localStorage instead, so the form always
 *   completes successfully from the frontend alone. Newsletter duplicate
 *   emails are detected locally and rejected with a 409, mirroring the
 *   shape callers already expect from the real API.
 */
export async function submitForm(endpoint, payload) {
  if (HAS_BACKEND) {
    return api.post(endpoint, payload);
  }

  await new Promise((resolve) => setTimeout(resolve, 500));

  if (endpoint === "/newsletter") {
    const existing = readLocal(endpoint);
    const email = String(payload?.email || "").toLowerCase();
    if (existing.some((e) => String(e.email || "").toLowerCase() === email)) {
      const err = new Error("Already subscribed");
      err.response = { status: 409, data: { detail: "Already subscribed" } };
      throw err;
    }
    writeLocal(endpoint, [...existing, { ...payload, submittedAt: new Date().toISOString() }]);
    return { data: { success: true, local: true } };
  }

  const existing = readLocal(endpoint);
  writeLocal(endpoint, [...existing, { ...payload, submittedAt: new Date().toISOString() }]);
  return { data: { success: true, local: true } };
}
